import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { OnboardingStepKey } from '../config/onboardingRegistry'

interface OnboardingGuidedState {
  active: boolean
  currentStep: OnboardingStepKey | null
  /** Oferty widoczne w hubie zarządzania podczas tutoriala. */
  hubOfferIds: string[] | null
  /** Pick zakończony (sukces, pusty wynik lub błąd) — nie blokuj listy w nieskończoność. */
  hubPickResolved: boolean
  /** Użytkownik próbował zaznaczyć więcej niż dozwolone w hubie tutorialu. */
  selectionLimitHint: boolean
  setSelectionLimitHint: (value: boolean) => void
  start: (stepKey?: OnboardingStepKey) => void
  setCurrentStep: (stepKey: OnboardingStepKey | null) => void
  setHubOfferIds: (ids: string[] | null) => void
  markHubPickResolved: () => void
  /** Activate session without wiping hubOfferIds when already active (module handoff). */
  ensure: (stepKey?: OnboardingStepKey) => void
  stop: () => void
}

export const useOnboardingGuidedStore = create<OnboardingGuidedState>()(
  persist(
    (set) => ({
      active: false,
      currentStep: null,
      hubOfferIds: null,
      hubPickResolved: false,
      selectionLimitHint: false,
      setSelectionLimitHint: (value) => set({ selectionLimitHint: value }),
      start: (stepKey) =>
        set({
          active: true,
          currentStep: stepKey ?? null,
          // Always reset hub pick — empty pick after Allegro connect must be retriable.
          hubOfferIds: null,
          hubPickResolved: false,
          selectionLimitHint: false,
        }),
      setCurrentStep: (stepKey) => set({ currentStep: stepKey }),
      setHubOfferIds: (ids) => set({ hubOfferIds: ids }),
      markHubPickResolved: () => set({ hubPickResolved: true }),
      ensure: (stepKey) =>
        set((state) => {
          if (state.active) {
            return {
              currentStep: stepKey ?? state.currentStep,
            }
          }
          return {
            active: true,
            currentStep: stepKey ?? null,
            hubOfferIds: null,
            hubPickResolved: false,
            selectionLimitHint: false,
          }
        }),
      stop: () =>
        set({
          active: false,
          currentStep: null,
          hubOfferIds: null,
          hubPickResolved: false,
          selectionLimitHint: false,
        }),
    }),
    {
      name: 'onboarding-guided-session',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        active: state.active,
        currentStep: state.currentStep,
        hubOfferIds: state.hubOfferIds,
        hubPickResolved: state.hubPickResolved,
      }),
    },
  ),
)

const GUIDED_SESSION_KEY = 'onboarding-guided-session'

/** Keep wizard clamp pool in sync with OM → module deep-link selection. */
export function syncGuidedHubOffers(offerIds: string[]): void {
  const state = useOnboardingGuidedStore.getState()
  if (!state.active || offerIds.length === 0) return
  state.setHubOfferIds(offerIds)
}

/** Synchroniczny odczyt sesji przewodnika (przed rehydracją Zustand po F5). */
export function readPersistedGuidedSession(): Pick<
  OnboardingGuidedState,
  'active' | 'currentStep'
> {
  try {
    const raw = sessionStorage.getItem(GUIDED_SESSION_KEY)
    if (!raw) return { active: false, currentStep: null }
    const parsed = JSON.parse(raw) as { state?: Partial<OnboardingGuidedState> }
    return {
      active: parsed.state?.active === true,
      currentStep: parsed.state?.currentStep ?? null,
    }
  } catch {
    return { active: false, currentStep: null }
  }
}
